import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import NavBar from '../components/layout/NavBar'
import { getToken } from '../lib/auth'
import { useMe } from '../hooks/useMe'
import { fetchListings, type Listing } from '../api/store'

export default function StoreListIntro() {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const displayName = me?.profile?.display_name || me?.username || 'Seller'

  const [popular, setPopular] = useState<any[] | null>(null)
  const [myListings, setMyListings] = useState<Listing[]>([])
  const [loadingMine, setLoadingMine] = useState(false)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await fetchListings()
        if (!cancelled) setPopular(data.slice(0, 8))
      } catch {}
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!authed) {
      setMyListings([])
      return
    }
    let cancelled = false
    setLoadingMine(true)
    fetchListings({ mine: true })
      .then((list) => { if (!cancelled) setMyListings(list) })
      .catch(() => { if (!cancelled) setMyListings([]) })
      .finally(() => { if (!cancelled) setLoadingMine(false) })
    return () => { cancelled = true }
  }, [authed])
  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-7xl px-4 py-10">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-[radial-gradient(80%_80%_at_10%_-10%,rgba(99,102,241,0.25),transparent_60%)] p-8">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Sell what you make — {displayName}</h1>
          <p className="mt-2 text-white/85 max-w-2xl">Create a clean, trustworthy product page in minutes. Use AI to craft your description, turn a phone photo into a studio‑style image, set price and stock, then share a single link.</p>
          <div className="mt-6 flex gap-3">
            <Link to={authed ? '/store/list/new' : '/login'} className="inline-flex items-center rounded-xl bg-brand px-5 py-3 font-semibold shadow hover:bg-brand-600">
              {authed ? 'Create Listing' : 'Sign in to list'}
            </Link>
            <a href="#how" className="inline-flex items-center rounded-xl border border-white/15 px-5 py-3 font-semibold hover:bg-white/5">How it works</a>
          </div>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-white/80">
            <li className="glass rounded-lg p-3">AI‑assisted description writing</li>
            <li className="glass rounded-lg p-3">Studio‑style product images</li>
            <li className="glass rounded-lg p-3">Price, stock, and condition</li>
            <li className="glass rounded-lg p-3">Share anywhere with one link</li>
          </ul>
        </section>

        {authed && (
          <section className="mt-8">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-xl font-extrabold">Your listings</h2>
              <Link to="/store/list/new" className="text-sm text-white/80 hover:text-white">Add listing</Link>
            </div>
            {loadingMine ? (
              <div className="rounded-2xl border border-white/10 p-6 text-center text-sm text-white/70">Loading your listings…</div>
            ) : myListings.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myListings.map((listing) => {
                  const thumb = listing.images?.[0]?.public_url || listing.product?.thumbnail_media?.public_url
                  const moderationState = listing.moderation_state || 'ok'
                  const showModeration = moderationState !== 'ok'
                  const moderationLabel =
                    moderationState === 'pending_review'
                      ? 'Pending review'
                      : moderationState === 'requires_edit'
                        ? 'Requires edit'
                        : moderationState === 'removed'
                          ? 'Removed'
                          : 'OK'
                  return (
                  <div key={listing.public_id} className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
                    <div className="text-sm text-white/60 uppercase tracking-[0.25em]">Listing</div>
                    <div className="rounded-xl bg-white/5 overflow-hidden">
                      {thumb ? (
                        <img src={thumb} alt={listing.product?.title || 'Listing thumbnail'} className="w-full aspect-video object-cover" />
                      ) : (
                        <div className="aspect-video bg-gradient-to-br from-white/10 to-white/5" />
                      )}
                    </div>
                    <div className="text-lg font-semibold">{listing.product?.title || 'Untitled product'}</div>
                    <div className="text-sm text-white/70">Price: {listing.price_pixe ? `${listing.price_pixe} PIXE` : '—'} · Stock: {listing.stock_qty ?? '—'}</div>
                    <div className="flex items-center justify-between text-xs text-white/60">
                      <span className={listing.active ? 'text-emerald-300' : 'text-amber-300'}>
                        {listing.active ? 'Listed' : 'Unlisted'}
                      </span>
                      {showModeration && (
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] ${
                          moderationState === 'requires_edit'
                            ? 'border-red-400/40 text-red-200 bg-red-500/10'
                            : moderationState === 'removed'
                              ? 'border-white/20 text-white/70 bg-white/5'
                              : 'border-amber-400/30 text-amber-200 bg-amber-500/10'
                        }`}>
                          {moderationLabel}
                        </span>
                      )}
                      <div className="flex items-center gap-2">
                        <Link to={`/store/i/${listing.public_id}`} className="hover:text-white">View</Link>
                        <Link to={`/store/list/new?listing=${listing.public_id}`} className="inline-flex items-center rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600">
                          Edit
                        </Link>
                      </div>
                    </div>
                  </div>
                )})}
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 p-6 text-sm text-white/70">
                You haven’t listed anything yet. Create your first product to see it here.
              </div>
            )}
          </section>
        )}

        {/*
        <section className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-extrabold">Start from a template</h2>
            <Link to="/store/list/new" className="text-sm text-white/80 hover:text-white">Open blank</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <TemplateCard label="Used gear" desc="Bike, camera, console" to="/store/list/new?template=used" />
            <TemplateCard label="Apparel" desc="New clothing & merch" to="/store/list/new?template=apparel" />
            <TemplateCard label="Electronics" desc="Refurb or like—new" to="/store/list/new?template=electronics" />
            <TemplateCard label="Digital" desc="Download or license" to="/store/list/new?template=digital" />
            <TemplateCard label="Service" desc="Session or package" to="/store/list/new?template=service" />
          </div>
        </section>
        */}

        {/* Why / How */}
        <section id="how" className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <InfoCard title="Tell buyers what matters">Highlight the condition, key features, and what’s included. Keep it clear and confident.</InfoCard>
          <InfoCard title="Professional images">Use AI to create a clean background so your product pops. Upload a photo and enhance it.</InfoCard>
          <InfoCard title="Set price + stock">Make it available now. Buyers see clear pricing and an easy checkout.</InfoCard>
        </section>

        {/* Examples */}
        {popular?.length ? (
          <section className="mt-10">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-xl font-extrabold">Examples from the Store</h2>
              <Link to="/store" className="text-sm text-white/80 hover:text-white">Browse store</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {popular.slice(0,8).map((l: any, i: number) => {
                const img = l.images?.[0]?.public_url || l.product?.thumbnail_media?.public_url
                const title = l.product?.title || 'Listing'
                const href = `/store/i/${l.public_id}`
                return (
                  <a key={l.public_id ?? i} href={href} className="glass rounded-xl overflow-hidden block hover:shadow-lg hover:shadow-black/20">
                    <div className="aspect-[16/9] bg-white/5">
                      {img ? <img src={img} className="h-full w-full object-cover" /> : null}
                    </div>
                    <div className="p-3 text-sm font-semibold">{title}</div>
                  </a>
                )
              })}
            </div>
          </section>
        ) : null}

        {/* CTA */}
        <section className="mt-10">
          <div className="rounded-2xl border border-white/10 p-6 flex items-center justify-between">
            <div>
              <div className="text-white font-extrabold">Ready to list a product?</div>
              <div className="text-white/80">Create your listing with AI help for copy and images.</div>
            </div>
            <Link to={authed ? '/store/list/new' : '/login'} className="inline-flex items-center rounded-xl bg-brand px-4 py-2 font-semibold shadow hover:bg-brand-600">
              {authed ? 'Create Listing' : 'Sign in'}
            </Link>
          </div>
        </section>
      </main>
    </div>
  )
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 p-4">
      <div className="text-white font-semibold mb-1">{title}</div>
      <div className="text-white/80">{children}</div>
    </div>
  )
}

function TemplateCard({ label, desc, to }: { label: string; desc: string; to: string }) {
  return (
    <Link to={to} className="group rounded-2xl border border-white/10 p-4 hover:bg-white/5 transition">
      <div className="h-32 rounded-lg bg-[linear-gradient(135deg,rgba(255,255,255,0.08),transparent)]" />
      <div className="mt-3 font-semibold">{label}</div>
      <div className="text-sm text-white/70">{desc}</div>
      <div className="mt-3 inline-flex items-center text-sm text-brand-400 group-hover:underline">Use template</div>
    </Link>
  )
}
